import { crush } from 'radash';
import React, { useState, useEffect } from 'react';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { unparse } from 'papaparse';
import { IoMdDownload } from 'react-icons/io';
import { FaMagnifyingGlass } from 'react-icons/fa6';
import { Button, Check } from '../input';
import { Modal } from '../modal';

export const resultBoxStyle = {
  padding: 10,
  background: '#f6f6f6',
  border: '1px solid #eee',
  borderRadius: 5,
};

export const resultHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 5,
  fontSize: 14,
};

export const ItemsTable = ({ items }) => {
  const [showHidden, setShowHidden] = useState(false);
  const columnHelper = createColumnHelper();

  const handleCsv = () => {
    const rows = table.getRowModel().rows;

    const data = rows.map((row) => {
      const obj = {};
      row.getVisibleCells().forEach((cell) => {
        obj[cell.column.id] = cell.getValue();
      });
      return crush(obj);
    });

    const csv = unparse(data); // handles escaping, quoting, etc.

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'extact.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleJsonl = () => {
    const rows = table.getRowModel().rows;

    const jsonl = rows
      .map((row) => {
        const obj = {};
        row.getVisibleCells().forEach((cell) => {
          obj[cell.column.id] = cell.getValue();
        });
        return JSON.stringify(obj);
      })
      .join('\n');

    const blob = new Blob([jsonl], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'extract.jsonl';
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns = React.useMemo(() => {
    if (!items || items.length === 0) return [];
    return Object.keys(items[0])
      .filter((key) => showHidden || !key.startsWith('_'))
      .map((key) =>
        columnHelper.accessor(key, {
          header: key,
          cell: (info) => {
            const val = info.getValue();
            if (typeof val == 'string' && val.startsWith('http')) {
              return (
                <a href={val} target="_blank" rel="noopener noreferrer">
                  {val}
                </a>
              );
            } else if (typeof val == 'string') {
              return val;
            } else {
              return JSON.stringify(val);
            }
          },
        }),
      );
  }, [items, showHidden]);

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
  });

  if (!items || items.length === 0) return <div>No items yet</div>;

  const modal = (item) => (
    <Modal
      title={
        <div>
          <b>Details</b>
        </div>
      }
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, width: 20 }}>
        <FaMagnifyingGlass />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <pre>{JSON.stringify(item, null, 2)}</pre>
      </div>
    </Modal>
  );

  const padding = 4;

  return (
    <div>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          background: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 5,
        }}
      >
        <div style={{ display: 'flex', gap: 10 }}>
          <Check useSwitch size="sm" value={showHidden} onChange={setShowHidden}>
            Show hidden fields
          </Check>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div>
            {items.length} {items.length == 1 ? 'item' : 'items'}
          </div>

          <Button small grayFlat onClick={handleCsv}>
            <IoMdDownload />
            CSV
          </Button>
          <Button small grayFlat onClick={handleJsonl}>
            <IoMdDownload /> JSONL
          </Button>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ fontSize: 12, tableLayout: 'auto', width: 'max-content', marginx: '10px 0' }}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                <th></th>
                {headerGroup.headers.map((header) => (
                  <th style={{ padding }} key={header.id}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, idx) => (
              <tr key={row.id}>
                <td style={{}}>{modal(items[idx])}</td>
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    style={{
                      whiteSpace: 'nowrap',
                      maxWidth: 400,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      padding,
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
